import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, Edit, BookOpen, Clock, Loader2, Trash2, GraduationCap } from 'lucide-react';
import { canEdit } from '@/lib/auth';
import api from '@/lib/api';

export default function Curriculums() {
  const [curriculums, setCurriculums] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState(null);
  const [seeding, setSeeding] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '', course_type: '', active: true, shared_all_companies: false,
    sequential_subjects: false, use_simulation_for_average: false, notes: ''
  });

  const [subjectFormData, setSubjectFormData] = useState({
    curriculum_id: '', code: '', name: '', description: '',
    weekly_hours: '', total_hours: '', module: '', evaluation_system: '', order: 0
  });

  const courseTypes = ['Educação Infantil', 'Ensino Fundamental', 'Ensino Médio'];

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [currRes, subjRes] = await Promise.all([
        api.get('/curriculums'),
        api.get('/curriculum-subjects')
      ]);
      setCurriculums(currRes.data);
      setSubjects(subjRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', course_type: '', active: true, shared_all_companies: false,
      sequential_subjects: false, use_simulation_for_average: false, notes: ''
    });
    setEditing(null);
  };

  const resetSubjectForm = () => {
    setSubjectFormData({
      curriculum_id: selectedCurriculum?.id || '', code: '', name: '', description: '',
      weekly_hours: '', total_hours: '', module: '', evaluation_system: '', order: 0
    });
    setEditingSubject(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.course_type) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    try {
      if (editing) {
        await api.put(`/curriculums/${editing.id}`, formData);
        toast.success('Matriz atualizada!');
      } else {
        await api.post('/curriculums', formData);
        toast.success('Matriz criada!');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar matriz');
    }
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();
    if (!subjectFormData.code || !subjectFormData.name) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    try {
      const data = { ...subjectFormData, curriculum_id: selectedCurriculum.id };
      if (editingSubject) {
        await api.put(`/curriculum-subjects/${editingSubject.id}`, data);
        toast.success('Disciplina atualizada!');
      } else {
        await api.post('/curriculum-subjects', data);
        toast.success('Disciplina criada!');
      }
      setSubjectDialogOpen(false);
      resetSubjectForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar disciplina');
    }
  };

  const handleEdit = (curriculum) => {
    setEditing(curriculum);
    setFormData({ ...curriculum });
    setSelectedCurriculum(curriculum);
    setDialogOpen(true);
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
    setSubjectFormData({ ...subject });
    setSubjectDialogOpen(true);
  };

  const handleDeleteSubject = async (subjectId) => {
    if (!confirm('Tem certeza que deseja excluir esta disciplina?')) return;
    try {
      await api.delete(`/curriculum-subjects/${subjectId}`);
      toast.success('Disciplina excluída!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir disciplina');
    }
  };

  const handleSeedDefault = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/curriculums/seed-default');
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar matrizes');
    } finally {
      setSeeding(false);
    }
  };

  const openCurriculumDetail = (curriculum) => {
    setSelectedCurriculum(curriculum);
    setEditing(curriculum);
    setFormData({ ...curriculum });
    setDialogOpen(true);
  };

  const filteredCurriculums = curriculums.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === 'all' || c.course_type === filterType;
    return matchesSearch && matchesType;
  });

  const getCurriculumSubjects = (curriculumId) => {
    return subjects.filter(s => s.curriculum_id === curriculumId).sort((a, b) => a.order - b.order);
  };

  const getTypeBadge = (type) => {
    const colors = {
      'Educação Infantil': 'bg-pink-100 text-pink-700',
      'Ensino Fundamental': 'bg-blue-100 text-blue-700',
      'Ensino Médio': 'bg-purple-100 text-purple-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="p-6 space-y-6" data-testid="curriculums-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Matrizes Curriculares</h1>
          <p className="text-muted-foreground mt-1">Gerenciar grades curriculares e disciplinas</p>
        </div>
        {canEdit() && (
          <div className="flex gap-2">
            {curriculums.length === 0 && (
              <Button variant="outline" onClick={handleSeedDefault} disabled={seeding}>
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GraduationCap className="w-4 h-4 mr-2" />}
                Criar Matrizes Padrão
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { resetForm(); setSelectedCurriculum(null); } }}>
              <DialogTrigger asChild>
                <Button data-testid="new-curriculum-button" onClick={() => { resetForm(); setSelectedCurriculum(null); }}>
                  <Plus className="w-4 h-4 mr-2" /> Nova Matriz
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editing ? `Matriz: ${editing.name}` : 'Nova Matriz Curricular'}</DialogTitle>
                  <DialogDescription>Configure a matriz e suas disciplinas</DialogDescription>
                </DialogHeader>
                
                {!editing ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Nome da Matriz *</Label>
                        <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Ex: 1º Ano E.F1 - 2026" required />
                      </div>
                      <div>
                        <Label>Tipo do Curso *</Label>
                        <Select value={formData.course_type || undefined} onValueChange={(v) => setFormData({...formData, course_type: v})}>
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            {courseTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col justify-end">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={formData.active} onCheckedChange={(c) => setFormData({...formData, active: c})} />
                          <Label>Ativa</Label>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={formData.sequential_subjects} onCheckedChange={(c) => setFormData({...formData, sequential_subjects: c})} />
                        <Label>Disciplinas Sequenciais</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox checked={formData.use_simulation_for_average} onCheckedChange={(c) => setFormData({...formData, use_simulation_for_average: c})} />
                        <Label>Simulado para compor média</Label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit">Criar</Button>
                    </DialogFooter>
                  </form>
                ) : (
                  <div className="space-y-4">
                    {/* Info da Matriz */}
                    <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                      <div>
                        <Label className="text-xs text-muted-foreground">Tipo do Curso</Label>
                        <p className="font-medium">{selectedCurriculum?.course_type}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Status</Label>
                        <Badge className={selectedCurriculum?.active ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>{selectedCurriculum?.active ? 'Ativa' : 'Inativa'}</Badge>
                      </div>
                    </div>

                    {/* Disciplinas */}
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold">Disciplinas</h3>
                      <Button size="sm" onClick={() => { resetSubjectForm(); setSubjectDialogOpen(true); }}>
                        <Plus className="w-4 h-4 mr-1" /> Adicionar
                      </Button>
                    </div>

                    {getCurriculumSubjects(selectedCurriculum?.id).length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma disciplina cadastrada</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-20">Código</TableHead>
                            <TableHead>Disciplina</TableHead>
                            <TableHead className="w-28">CH Semanal</TableHead>
                            <TableHead className="w-28">CH Total</TableHead>
                            <TableHead className="w-20">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getCurriculumSubjects(selectedCurriculum?.id).map(subj => (
                            <TableRow key={subj.id}>
                              <TableCell className="font-mono text-xs">{subj.code}</TableCell>
                              <TableCell className="font-medium">{subj.name}</TableCell>
                              <TableCell>{subj.weekly_hours}</TableCell>
                              <TableCell>{subj.total_hours}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => handleEditSubject(subj)}><Edit className="w-4 h-4" /></Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteSubject(subj.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Filtros */}
      <Card className="border-border/60">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar matriz..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Tipos</SelectItem>
                {courseTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border/60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg"><BookOpen className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Total de Matrizes</p>
              <p className="text-2xl font-bold">{filteredCurriculums.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg"><Clock className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Total de Disciplinas</p>
              <p className="text-2xl font-bold">{subjects.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg"><GraduationCap className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Matrizes Ativas</p>
              <p className="text-2xl font-bold">{filteredCurriculums.filter(c => c.active).length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Matrizes */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : filteredCurriculums.length === 0 ? (
        <Card className="border-border/60">
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Nenhuma matriz encontrada</p>
            {canEdit() && (
              <Button className="mt-4" onClick={handleSeedDefault} disabled={seeding}>
                Criar Matrizes Padrão
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCurriculums.map(curriculum => {
            const currSubjects = getCurriculumSubjects(curriculum.id);
            return (
              <Card key={curriculum.id} className="border-border/60 hover:border-primary/50 transition-colors cursor-pointer" onClick={() => openCurriculumDetail(curriculum)}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <Badge className={getTypeBadge(curriculum.course_type)}>{curriculum.course_type}</Badge>
                    <Badge variant={curriculum.active ? 'default' : 'secondary'}>{curriculum.active ? 'Ativa' : 'Inativa'}</Badge>
                  </div>
                  <CardTitle className="text-lg mt-2">{curriculum.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <BookOpen className="w-4 h-4" />
                    <span>{currSubjects.length} disciplinas</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog de Disciplina */}
      <Dialog open={subjectDialogOpen} onOpenChange={(open) => { setSubjectDialogOpen(open); if (!open) resetSubjectForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSubject ? 'Editar Disciplina' : 'Nova Disciplina'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubjectSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input value={subjectFormData.code} onChange={(e) => setSubjectFormData({...subjectFormData, code: e.target.value})} placeholder="PORT" required />
              </div>
              <div>
                <Label>Ordem</Label>
                <Input type="number" value={subjectFormData.order} onChange={(e) => setSubjectFormData({...subjectFormData, order: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div>
              <Label>Nome da Disciplina *</Label>
              <Input value={subjectFormData.name} onChange={(e) => setSubjectFormData({...subjectFormData, name: e.target.value})} placeholder="Língua Portuguesa" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CH Semanal</Label>
                <Input value={subjectFormData.weekly_hours} onChange={(e) => setSubjectFormData({...subjectFormData, weekly_hours: e.target.value})} placeholder="06:00" />
              </div>
              <div>
                <Label>CH Total</Label>
                <Input value={subjectFormData.total_hours} onChange={(e) => setSubjectFormData({...subjectFormData, total_hours: e.target.value})} placeholder="0240:00" />
              </div>
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={subjectFormData.description} onChange={(e) => setSubjectFormData({...subjectFormData, description: e.target.value})} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSubjectDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">{editingSubject ? 'Salvar' : 'Criar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
